import { CheckCircleSolid, ChevronDown, ChevronRight, ExclamationCircle } from "@medusajs/icons"
import { Button, clx, Skeleton, Text, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState, type ReactNode } from "react"
import { Link, useNavigate } from "react-router-dom"
import { GlobalSearch } from "../components/global-search"
import { PageHeader } from "../components/page"
import { Stat } from "../components/ui"
import { useAuth } from "../lib/auth"
import { bookingApi, dayLabel } from "../lib/booking"
import { formatDate, formatDateWith, formatPercent, money0, QUEUE_LABEL } from "../lib/format"
import { caps, getLocale, t, tc, tk, ts } from "../lib/i18n"
import { useFavourites } from "../lib/favourites"
import { errorMessage, hqApi, type HqAlert, type PulseWindow } from "../lib/medusa"
import { fetchVisitors } from "../lib/visitors"
import { usePicks } from "../components/saved-picks"
import { courierApi } from "../lib/courier"
import { daysWithoutParcel, NO_PARCEL_DAYS } from "../lib/custom-message"
import { deliveryOf } from "../lib/custom-filters"
import { useMyRole } from "../lib/roles"
import { newWorkbenchId, useOpenOrders } from "../lib/workbench"

const SEVERITY: Record<HqAlert["severity"], { label: string; dot: string; text: string }> = {
  urgent: { label: tk("Urgent"), dot: "bg-ui-tag-red-icon", text: "text-ui-tag-red-text" },
  today: { label: tk("Do today"), dot: "bg-ui-tag-orange-icon", text: "text-ui-tag-orange-text" },
  fyi: { label: tk("For info"), dot: "bg-ui-tag-neutral-icon", text: "text-ui-fg-muted" },
}

/**
 * When three or more alerts of one kind are open they become one line ("39 meet-ups and home
 * deliveries not handed over") that opens the list behind it, so one backlog cannot bury the
 * card that was charged without an order.
 */
const GROUPS: Record<string, { label: (n: number) => string; to: string }> = {
  to_ship: { label: (n) => t.n(n, "{n} BOX NOW order not shipped", "{n} BOX NOW orders not shipped"), to: "/orders?queue=to_ship" },
  to_arrange: { label: (n) => t.n(n, "{n} meet-up or home delivery not handed over", "{n} meet-ups and home deliveries not handed over"), to: "/orders?queue=to_arrange" },
  cash_to_collect: { label: (n) => t.n(n, "{n} order with the cash not recorded", "{n} orders with the cash not recorded"), to: "/orders?queue=cash_to_collect" },
  sold_out: { label: (n) => t.n(n, "{n} product that sells is sold out", "{n} products that sell are sold out"), to: "/stock" },
  low_stock: { label: (n) => t.n(n, "{n} product running low", "{n} products running low"), to: "/stock" },
  link_paid: { label: (n) => t.n(n, "{n} payment link paid", "{n} payment links paid"), to: "/payments" },
  custom_no_parcel: { label: (n) => t.n(n, "{n} custom order 20+ days without a parcel", "{n} custom orders 20+ days without a parcel"), to: "/custom" },
}
const GROUP_AT = 3
const SHOWN = 6

const athensToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Athens" }).format(new Date())
const athensHour = () => Number(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Athens", hour: "2-digit", hourCycle: "h23" }).format(new Date()))

/**
 * A Greek first name as it is said to the person (the vocative): Αντώνης → Αντώνη, Κώστας → Κώστα,
 * Γιώργος → Γιώργο, Άγγελος → Άγγελε. A name in Latin letters, or with any other ending, stays as it is.
 */
function vocative(name: string): string {
  if (!/^\p{Script=Greek}+$/u.test(name)) return name
  if (/[ηήαά]ς$/.test(name)) return name.slice(0, -1)
  if (!name.endsWith("ος")) return name
  // Two syllables take -ο (Γιώργο, Νίκο), longer names -ε (Άγγελε, Δημήτριε). The second letter of
  // αι, ει, οι, αυ, ευ, ου is no syllable of its own, nor is an unaccented ι before a vowel (Γιώργος).
  const letters: Array<{ c: string; accent: boolean; apart: boolean }> = []
  for (const char of name.toLowerCase().normalize("NFD")) {
    const last = letters[letters.length - 1]
    if (char === "́" && last) last.accent = true
    else if (char === "̈" && last) last.apart = true
    else if (!/\p{M}/u.test(char)) letters.push({ c: char, accent: false, apart: false })
  }
  const vowel = (c: string | undefined) => !!c && "αεηιουω".includes(c)
  let syllables = 0
  letters.forEach((letter, i) => {
    if (!vowel(letter.c)) return
    const before = letters[i - 1]
    const pair = !letter.apart && !!before && !before.accent && "ιυ".includes(letter.c) && "αεο".includes(before.c)
    const glide = letter.c === "ι" && !letter.accent && !letter.apart && vowel(letters[i + 1]?.c)
    if (!pair && !glide) syllables++
  })
  return name.slice(0, -2) + (syllables <= 2 ? "ο" : "ε")
}

/**
 * Today — everyone's first screen: find anything, what is waiting to be done (each count opens its
 * list), what needs attention, and how the month is going.
 */
export function TodayPage() {
  const { user } = useAuth()
  const firstName = user?.first_name || user?.email?.split("@")[0] || ""
  const name = getLocale() === "el" ? vocative(firstName) : firstName
  const hour = athensHour()
  const { role, loading: roleLoading } = useMyRole()
  const greeting = hour < 5 ? t("Good night") : hour < 12 ? t("Good morning") : hour < 18 ? t("Good afternoon") : t("Good evening")

  return (
    <div className="flex flex-col gap-y-6">
      <PageHeader
        title={`${greeting}${name ? `, ${name}` : ""}`}
        name={t("Today")}
        eyebrow={formatDate(new Date(), "weekdayLong")}
      />
      <GlobalSearch />
      {roleLoading ? (
        <Skeleton className="h-40 w-full rounded-lg" />
      ) : role === "custom" ? (
        <CustomToday />
      ) : role === "courier" ? (
        <CourierToday />
      ) : (
        <>
          <ToDo />
          <Attention />
          <SalesPulse />
          <ThisMonth />
        </>
      )}
      <QuickAccess />
      {role === "owner" && <SystemLine />}
    </div>
  )
}

/** A section's printed label; its text goes through caps(), so Greek capitals carry no accents. */
function SectionLabel({ children, action }: { children: string; action?: ReactNode }) {
  return (
    <div className="flex min-h-[20px] items-end justify-between gap-x-2 px-1">
      <h2 className="hc-eyebrow">{caps(children)}</h2>
      {action}
    </div>
  )
}

/** The work waiting, each count the way into its list. */
function ToDo() {
  const orders = useQuery({ queryKey: ["hq", "orders"], queryFn: hqApi.orders, refetchInterval: 60_000 })
  const custom = useQuery({ queryKey: ["hq", "custom"], queryFn: hqApi.customOrders, refetchInterval: 120_000 })
  const counts = orders.data?.counts
  const toDeliver = custom.data ? custom.data.orders.filter((order) => order.status === "pending" && !order.is_stock).length : null
  const value = (count: number | null | undefined) => (count == null ? "–" : count)
  const tone = (count: number | null | undefined) => (count ? "default" : "muted") as "default" | "muted"
  return (
    <section className="flex flex-col gap-y-2">
      <SectionLabel>{t("To do")}</SectionLabel>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Stat label={QUEUE_LABEL.to_ship} value={value(counts?.to_ship)} tone={tone(counts?.to_ship)} hint={t("BOX NOW, waiting for a voucher")} to="/orders?queue=to_ship" />
        <Stat label={QUEUE_LABEL.to_arrange} value={value(counts?.to_arrange)} tone={tone(counts?.to_arrange)} hint={t("Meet-ups and home deliveries")} to="/orders?queue=to_arrange" />
        <Stat label={QUEUE_LABEL.cash_to_collect} value={value(counts?.cash_to_collect)} tone={tone(counts?.cash_to_collect)} hint={t("Handed over, cash not recorded")} to="/orders?queue=cash_to_collect" />
        <Stat label={t("Custom to deliver")} value={value(toDeliver)} tone={tone(toDeliver)} hint={t("Arrived, not with the customer yet")} to="/custom?tab=pending" />
      </div>
      <Deliveries />
    </section>
  )
}

/** Today's meet-ups and home stops from the booking system, or the next day that has some. */
function Deliveries() {
  const overview = useQuery({ queryKey: ["booking", "overview"], queryFn: bookingApi.overview, refetchInterval: 60_000, retry: false })
  if (!overview.data) return null
  const data = overview.data
  const days = new Map<string, { meetups: number; stops: number }>()
  for (const day of data.meetupDays) days.set(day.date, { meetups: day.booked, stops: days.get(day.date)?.stops ?? 0 })
  for (const day of data.homeDays) days.set(day.date, { meetups: days.get(day.date)?.meetups ?? 0, stops: day.stops.length })
  const today = days.get(data.today)
  const next = [...days.entries()].filter(([date, day]) => date > data.today && day.meetups + day.stops > 0).sort(([a], [b]) => a.localeCompare(b))[0]
  const words = (day: { meetups: number; stops: number }) =>
    [day.meetups ? t.n(day.meetups, "{n} meet-up", "{n} meet-ups") : null, day.stops ? t.n(day.stops, "{n} home stop", "{n} home stops") : null].filter(Boolean).join(" · ")
  const busyToday = !!today && today.meetups + today.stops > 0
  const needCall = data.needsAttention.length

  return (
    <Link
      to={busyToday ? `/booking/day/${data.today}` : next ? `/booking/day/${next[0]}` : "/booking"}
      className="bg-ui-bg-base shadow-elevation-card-rest hover:shadow-elevation-card-hover active:bg-ui-bg-base-pressed flex items-center gap-x-3 rounded-lg p-3 transition-shadow"
    >
      <div className="min-w-0 flex-1">
        <p className="hc-eyebrow">{caps(t("Deliveries"))}</p>
        <p className="txt-compact-medium-plus mt-1.5">
          {busyToday && today
            ? t("Today: {what}", { what: words(today) })
            : next
              ? t("Nothing today · {day}: {what}", { day: dayLabel(next[0]), what: words(next[1]) })
              : t("Nothing booked yet")}
        </p>
        {needCall > 0 && (
          <p className="txt-compact-small text-ui-tag-orange-text mt-1">
            {t.n(needCall, "{n} booking needs a call", "{n} bookings need a call")}
          </p>
        )}
      </div>
      <span className="txt-compact-small-plus text-ui-fg-interactive shrink-0">{busyToday ? t("Route") : tc("action", "Open")}</span>
      <ChevronRight className="text-ui-fg-muted -ml-2 shrink-0" />
    </Link>
  )
}

type Row = { kind: "one"; alert: HqAlert } | { kind: "group"; key: string; severity: HqAlert["severity"]; alerts: HqAlert[] }

function rowsOf(alerts: HqAlert[]): Row[] {
  const byKind = new Map<string, HqAlert[]>()
  for (const alert of alerts) byKind.set(alert.kind, [...(byKind.get(alert.kind) ?? []), alert])
  const rows: Row[] = []
  const grouped = new Set<string>()
  for (const alert of alerts) {
    const same = byKind.get(alert.kind) ?? []
    if (GROUPS[alert.kind] && same.length >= GROUP_AT) {
      if (!grouped.has(alert.kind)) {
        grouped.add(alert.kind)
        rows.push({ kind: "group", key: alert.kind, severity: alert.severity, alerts: same })
      }
    } else {
      rows.push({ kind: "one", alert })
    }
  }
  const rank = { urgent: 0, today: 1, fyi: 2 }
  const severityOf = (row: Row) => (row.kind === "one" ? row.alert.severity : row.severity)
  return rows.sort((a, b) => rank[severityOf(a)] - rank[severityOf(b)])
}

/**
 * What needs attention (checked every 10 minutes by the backend; urgent ones are also emailed at
 * once), and the morning email to the shop inbox — which can be sent now to see it.
 */
function Attention({ only }: { only?: string[] } = {}) {
  const queryClient = useQueryClient()
  const [all, setAll] = useState(false)
  const alerts = useQuery({ queryKey: ["hq", "alerts"], queryFn: hqApi.alerts, refetchInterval: 60_000 })
  const check = useMutation({
    mutationFn: (sendDigest: boolean) => hqApi.refreshAlerts(sendDigest),
    onSuccess: (result, sendDigest) => {
      void queryClient.invalidateQueries({ queryKey: ["hq", "alerts"] })
      if (sendDigest) toast[result.digest === "sent" ? "success" : "error"](result.digest === "sent" ? t("Morning email sent to the shop inbox.") : t("The email could not be sent. Try again in a minute."))
      else toast.success(result.open ? t.n(result.open, "{n} thing need attention.", "{n} things need attention.") : t("Nothing needs attention."))
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const list = (alerts.data?.alerts ?? []).filter((alert) => !only || only.includes(alert.kind))
  const rows = rowsOf(list)
  const shown = all ? rows : rows.slice(0, SHOWN)

  return (
    <section className="flex flex-col gap-y-2">
      <SectionLabel
        action={
          <button type="button" onClick={() => check.mutate(false)} disabled={check.isPending} className="txt-compact-small-plus text-ui-fg-interactive disabled:text-ui-fg-disabled">
            {check.isPending && check.variables === false ? t("Checking…") : t("Check now")}
          </button>
        }
      >
        {`${t("Needs attention")}${list.length ? ` · ${list.length}` : ""}`}
      </SectionLabel>
      <div className="bg-ui-bg-base shadow-elevation-card-rest overflow-hidden rounded-lg">
        {alerts.isLoading && <p className="txt-small text-ui-fg-muted px-4 py-3">{t("Checking…")}</p>}
        {alerts.isError && <p className="txt-small text-ui-fg-error px-4 py-3">{errorMessage(alerts.error)}</p>}
        {alerts.data && !list.length && (
          <div className="flex items-center gap-x-2 px-4 py-3">
            <CheckCircleSolid className="text-ui-tag-green-icon" />
            <Text size="small">{t("Nothing needs you right now.")}</Text>
          </div>
        )}
        {rows.length > 0 && (
          <ul className="divide-ui-border-base flex flex-col divide-y">
            {shown.map((row) => (row.kind === "one" ? <AlertRow key={row.alert.key} alert={row.alert} /> : <GroupRow key={row.key} row={row} />))}
          </ul>
        )}
        {rows.length > SHOWN && (
          <button type="button" onClick={() => setAll(!all)} className="border-ui-border-base txt-compact-small-plus text-ui-fg-interactive hover:bg-ui-bg-base-hover w-full border-t px-4 py-3 text-left">
            {all ? t("Show fewer") : t.n(rows.length - SHOWN, "Show {n} more", "Show {n} more")}
          </button>
        )}
      </div>
      {!only && <div className="flex flex-wrap items-center justify-between gap-x-2 px-1">
        <Text size="xsmall" className="text-ui-fg-muted">
          {alerts.data?.last_digest
            ? t("A morning email goes to the shop inbox at 8:00 (last {day}); urgent things are emailed at once.", { day: formatDate(`${alerts.data.last_digest.day}T12:00:00Z`, "day") })
            : t("A morning email goes to the shop inbox at 8:00; urgent things are emailed at once.")}
        </Text>
        <Button size="small" variant="transparent" className="text-ui-fg-interactive -mr-2" onClick={() => check.mutate(true)} isLoading={check.isPending && check.variables === true}>
          {t("Email it to me now")}
        </Button>
      </div>}
    </section>
  )
}

function SeverityMark({ severity }: { severity: HqAlert["severity"] }) {
  return (
    <span className={clx("hc-eyebrow inline-flex items-center gap-x-1.5", SEVERITY[severity].text)}>
      <span aria-hidden className={clx("size-2 rounded-[2px]", SEVERITY[severity].dot)} />
      {caps(t(SEVERITY[severity].label))}
    </span>
  )
}

/** One alert. Its title and detail are written by the server (backend lib/hq/alerts/rules.ts). */
function AlertRow({ alert }: { alert: HqAlert }) {
  const body = (
    <div className="flex min-w-0 flex-1 flex-col gap-y-1">
      <SeverityMark severity={alert.severity} />
      <span className="txt-compact-medium-plus">{ts(alert.title)}</span>
      <span className="txt-small text-ui-fg-subtle">{ts(alert.detail)}</span>
    </div>
  )
  return (
    <li className="px-4 py-3">
      {alert.link ? (
        <Link to={alert.link} className="hover:bg-ui-bg-base-hover -m-1.5 flex items-center gap-x-2 rounded-md p-1.5">
          {body}
          <ChevronRight className="text-ui-fg-muted shrink-0" />
        </Link>
      ) : (
        body
      )}
      {alert.kind === "paid_no_order" && <CompletePaidCart cartId={alert.key.replace(/^paid-no-order:/, "")} />}
    </li>
  )
}

function GroupRow({ row }: { row: Extract<Row, { kind: "group" }> }) {
  const [open, setOpen] = useState(false)
  const group = GROUPS[row.key]
  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-x-2">
        <Link to={group.to} className="hover:bg-ui-bg-base-hover -m-1.5 flex min-w-0 flex-1 flex-col gap-y-1 rounded-md p-1.5">
          <SeverityMark severity={row.severity} />
          <span className="txt-compact-medium-plus">{group.label(row.alerts.length)}</span>
        </Link>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="txt-compact-small-plus text-ui-fg-subtle hover:bg-ui-bg-base-hover border-ui-border-base flex h-8 shrink-0 items-center gap-x-1 rounded-full border px-3"
        >
          {open ? t("Hide") : t("List")} <ChevronDown className={clx("transition-transform", open && "rotate-180")} />
        </button>
      </div>
      {open && (
        <ul className="border-ui-border-base mt-2 flex flex-col border-l pl-3">
          {row.alerts.map((alert) => (
            <li key={alert.key}>
              {alert.link ? (
                <Link to={alert.link} className="txt-compact-small hover:text-ui-fg-interactive flex items-center justify-between gap-x-2 py-1.5">
                  <span className="min-w-0 truncate">{ts(alert.title)}</span>
                  <ChevronRight className="text-ui-fg-muted shrink-0" />
                </Link>
              ) : (
                <span className="txt-compact-small block py-1.5">{ts(alert.title)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * On the urgent "card charged but no order" alert: make the order from the same cart. The backend
 * checks the cart still holds a charged card and is not an order yet, and runs Medusa's own
 * completion, so the customer is never charged twice and gets the usual confirmation email.
 */
function CompletePaidCart({ cartId }: { cartId: string }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const prompt = usePrompt()
  const complete = useMutation({
    mutationFn: () => hqApi.completePaidCart(cartId),
    onSuccess: ({ order }) => {
      toast.success(t("Order {ref} made. The customer gets the confirmation email.", { ref: order.display_id != null ? `W-${order.display_id}` : "" }))
      void queryClient.invalidateQueries({ queryKey: ["hq", "alerts"] })
      void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
      void hqApi.refreshAlerts(false)
      navigate(`/orders/${order.order_id}`)
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  async function confirm() {
    const ok = await prompt({
      title: t("Make the order from this cart?"),
      description: t("It becomes a normal order with the payment already taken, and the customer gets the confirmation email. They are not charged again. If you would rather give the money back, refund it in Medusa admin instead."),
      confirmText: t("Make the order"),
      cancelText: t("Back"),
      variant: "confirmation",
    })
    if (ok) complete.mutate()
  }
  return (
    <div className="mt-2 flex flex-col gap-y-1">
      <Button size="small" className="w-fit" isLoading={complete.isPending} onClick={() => void confirm()}>{t("Complete the order")}</Button>
      {complete.isError && (
        <Text size="xsmall" className="text-ui-fg-error">{errorMessage(complete.error)} {t("Fix that, then press again: the customer is not charged twice.")}</Text>
      )}
    </div>
  )
}

/**
 * Sales made today, in the last 7 days and this month (owners, 15 Oct 2026): every order placed and
 * not cancelled, paid or not yet, site and custom together, and each apart underneath.
 */
function SalesPulse() {
  const pulse = useQuery({ queryKey: ["hq", "pulse"], queryFn: hqApi.pulse, refetchInterval: 60_000 })
  const p = pulse.data
  const vsLastMonth = (() => {
    if (!p) return undefined
    const now = p.month.all.revenue
    const before = p.last_month_same_days.all.revenue
    const lastName = formatDateWith(new Date(`${p.last_month_same_days.from}T12:00:00Z`), { month: "short", timeZone: "UTC" })
    const range = t("{month} 1–{day}", { month: lastName, day: Number(p.last_month_same_days.to.slice(8, 10)) })
    if (!before) return `${range}: ${money0(before)}`
    const change = Math.round(((now - before) / Math.abs(before)) * 100)
    return t("{change} on {range} ({before})", { change: `${change >= 0 ? "+" : "−"}${formatPercent(Math.abs(change) / 100)}`, range, before: money0(before) })
  })()
  return (
    <section className="flex flex-col gap-y-2">
      <SectionLabel action={<Link to="/analytics" className="txt-compact-small-plus text-ui-fg-interactive">{t("Analytics")}</Link>}>{t("Sales")}</SectionLabel>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <PulseCard label={t("Today")} w={p?.today} hint={p ? t("Yesterday: {amount}", { amount: money0(p.yesterday.all.revenue) }) : undefined} />
        <PulseCard label={t("Last 7 days")} w={p?.week} />
        <PulseCard label={t("This month")} w={p?.month} hint={vsLastMonth} />
      </div>
      {/* Athens' date worked out here, so the visitors are asked for beside the sales, not after them. */}
      <VisitorsToday day={p?.day ?? athensToday()} />
      <Text size="xsmall" className="text-ui-fg-muted px-1">
        {pulse.isError ? errorMessage(pulse.error) : t("Orders placed, paid or not yet; cancelled ones left out. Athens time.")}
      </Text>
    </section>
  )
}

/** Visits today from the shop's own counter, with the two rates that matter, opening Analytics → Visitors. */
function VisitorsToday({ day }: { day: string | null }) {
  const visitors = useQuery({ queryKey: ["hq", "visitors", day, day], queryFn: () => fetchVisitors(day!, day!), enabled: !!day, refetchInterval: 60_000, retry: false })
  const v = visitors.data?.totals
  if (!v) return null
  const pct = (n: number | null) => (n == null ? "–" : `${formatPercent(n / 100, 1)}`)
  return (
    <Link to="/analytics?view=visitors" className="bg-ui-bg-base shadow-elevation-card-rest hover:shadow-elevation-card-hover flex items-center justify-between gap-x-3 rounded-lg px-3 py-2.5">
      <span className="flex min-w-0 flex-col">
        <span className="hc-eyebrow">{caps(t("Visitors today"))}</span>
        <span className="txt-compact-small text-ui-fg-subtle">
          {t("{rate} added to cart · {conversion} bought", { rate: pct(v.add_to_cart_rate), conversion: pct(v.conversion_rate) })}
        </span>
      </span>
      <span className="hc-data shrink-0 text-[22px]">{v.visits}</span>
    </Link>
  )
}

/** One window: the total in large, how many orders, and site and custom side by side underneath. */
function PulseCard({ label, w, hint }: { label: string; w: PulseWindow | undefined; hint?: string }) {
  return (
    <div className="bg-ui-bg-base shadow-elevation-card-rest flex flex-col gap-y-2 rounded-lg p-3">
      <div className="flex items-baseline justify-between gap-x-2">
        <span className="hc-eyebrow">{caps(label)}</span>
        <span className="txt-compact-xsmall text-ui-fg-muted">{w ? t.n(w.all.orders, "{n} order", "{n} orders") : ""}</span>
      </div>
      <span className="hc-data text-[26px] leading-[30px]">{w ? money0(w.all.revenue) : "–"}</span>
      <div className="grid grid-cols-2 gap-1.5">
        {(["site", "custom"] as const).map((group) => (
          <div key={group} className="bg-ui-bg-subtle flex min-w-0 flex-col rounded-md px-2 py-1.5">
            <span className="txt-compact-xsmall text-ui-fg-subtle">{group === "site" ? t("Site") : t("Custom")}</span>
            <span className="hc-data truncate text-[13px]">
              {w ? money0(w[group].revenue) : "–"}
              <span className="text-ui-fg-muted"> · {w ? w[group].orders : "–"}</span>
            </span>
          </div>
        ))}
      </div>
      {hint && <span className="txt-compact-xsmall text-ui-fg-muted">{hint}</span>}
    </div>
  )
}

/** This month so far against the same days of last month, so the comparison is fair on the 6th. */
function ThisMonth() {
  const today = athensToday()
  const monthStart = `${today.slice(0, 7)}-01`
  const day = Number(today.slice(8, 10))
  const lastStart = (() => {
    const date = new Date(`${monthStart}T12:00:00Z`)
    date.setUTCMonth(date.getUTCMonth() - 1)
    return date.toISOString().slice(0, 10)
  })()
  const lastSameDay = (() => {
    const date = new Date(`${lastStart}T12:00:00Z`)
    const daysInLast = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate()
    date.setUTCDate(Math.min(day, daysInLast))
    return date.toISOString().slice(0, 10)
  })()
  const channels: Array<"web" | "shopify" | "custom"> = ["web", "shopify", "custom"]
  const thisMonth = useQuery({ queryKey: ["hq", "sales", "today-this", monthStart, today], queryFn: () => hqApi.sales({ from: monthStart, to: today, channels }) })
  const lastMonth = useQuery({ queryKey: ["hq", "sales", "today-last", lastStart, lastSameDay], queryFn: () => hqApi.sales({ from: lastStart, to: lastSameDay, channels }) })
  const now = thisMonth.data?.totals
  const before = lastMonth.data?.totals
  // The month as it is written inside a date ("Sep 1–6" / «1–6 Σεπ»), not standing alone.
  const lastName = formatDateWith(new Date(`${lastStart}T12:00:00Z`), { month: "short", timeZone: "UTC" })
  const range = t("{month} 1–{day}", { month: lastName, day: Number(lastSameDay.slice(8, 10)) })
  const compare = (a: number | undefined, b: number | undefined, show: (n: number) => string) => {
    if (a == null || b == null) return undefined
    if (!b) return `${range}: ${show(b)}`
    const change = Math.round(((a - b) / Math.abs(b)) * 100)
    return t("{change} on {range} ({before})", { change: `${change >= 0 ? "+" : "−"}${formatPercent(Math.abs(change) / 100)}`, range, before: show(b) })
  }
  return (
    <section className="flex flex-col gap-y-2">
      <Stat label={t("Profit this month")} value={now ? money0(now.profit) : "–"} hint={compare(now?.profit, before?.profit, money0)} />
      <Text size="xsmall" className="text-ui-fg-muted px-1">
        {t("Site and custom orders whose costs are known, once paid. Athens time.")}
      </Text>
    </section>
  )
}

/** One quiet line when everything is connected; the problem, and where to look, when it is not. */
function SystemLine() {
  const health = useQuery({ queryKey: ["hq", "health"], queryFn: hqApi.health, refetchInterval: 60_000 })
  if (health.isError) {
    return (
      <p className="txt-small text-ui-fg-error flex items-center gap-x-2 px-1">
        <ExclamationCircle /> {t("HQ cannot reach the backend right now.")}
      </p>
    )
  }
  if (!health.data) return null
  const issues: string[] = []
  if (health.data.email_mode !== "live") issues.push(health.data.email_mode === "owner_only" ? t("Customer emails go to the shop inbox only (preview).") : t("Customer emails are off."))
  const sheets = health.data.custom_sheets
  if (sheets?.configured && sheets.larry_error) issues.push(t("Larry's sheet could not be read; shipping is estimated."))
  if (!issues.length) {
    return (
      <p className="txt-small text-ui-fg-muted flex items-center gap-x-1.5 px-1">
        <CheckCircleSolid className="text-ui-tag-green-icon" /> {t("Everything is connected.")}
        <Link to="/settings" className="text-ui-fg-interactive">{t("Details")}</Link>
      </p>
    )
  }
  return (
    <Link to="/settings" className="border-ui-tag-orange-border bg-ui-tag-orange-bg flex items-start gap-x-2 rounded-lg border px-3 py-2.5">
      <ExclamationCircle className="text-ui-tag-orange-icon mt-0.5 shrink-0" />
      <span className="txt-small text-ui-tag-orange-text flex-1">{issues.join(" ")}</span>
      <ChevronRight className="text-ui-tag-orange-icon mt-0.5 shrink-0" />
    </Link>
  )
}

/** The pages starred on More, one tap away (owners, 14 Oct 2026). Chosen per person, see lib/favourites.ts. */
function QuickAccess() {
  const { items } = useFavourites()
  return (
    <section className="flex flex-col gap-y-2" aria-labelledby="today-quick">
      <div className="flex items-baseline justify-between gap-x-3 px-1">
        <h2 id="today-quick" className="hc-eyebrow">{caps(t("Quick access"))}</h2>
        <Link to="/more" className="txt-compact-small text-ui-fg-interactive">{items.length ? t("Change") : t("Choose")}</Link>
      </div>
      {items.length ? (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((item) => (
            <li key={item.to}>
              <Link to={item.to} className="bg-ui-bg-base shadow-elevation-card-rest hover:bg-ui-bg-base-hover active:bg-ui-bg-base-pressed flex min-h-[52px] items-center gap-x-2.5 rounded-lg px-3 py-2">
                <span className="bg-ui-bg-subtle text-ui-fg-base flex size-8 shrink-0 items-center justify-center rounded-md">
                  <item.icon />
                </span>
                <span className="txt-compact-small-plus min-w-0 leading-tight">{t(item.label)}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="txt-small text-ui-fg-muted px-1">{t("Star the pages you use most on More, and they appear here.")}</p>
      )}
    </section>
  )
}

/** Alerts that belong to the custom-orders work (backend lib/hq/alerts/rules.ts kinds). */
const CUSTOM_ALERTS = ["custom_no_parcel", "custom_stuck", "link_paid", "deposit", "parcel_problem", "finder_store", "finder_categories"]
/** Alerts that belong to the deliveries. */
const COURIER_ALERTS = ["to_arrange", "cash_to_collect", "exchange_to_send"]

const depositPaid = (status: string) => status.trim().toUpperCase() === "PAID"

/** A big tap target that starts a piece of work. */
function Action({ to, onClick, label, hint, primary }: { to?: string; onClick?: () => void; label: string; hint?: string; primary?: boolean }) {
  const className = clx(
    "flex min-h-[64px] flex-col justify-center rounded-lg px-4 py-3 transition-shadow",
    primary ? "bg-ui-fg-base text-ui-bg-base" : "bg-ui-bg-base shadow-elevation-card-rest hover:shadow-elevation-card-hover"
  )
  const body = (
    <>
      <span className="txt-compact-medium-plus">{label}</span>
      {hint && <span className={clx("txt-compact-xsmall mt-0.5", primary ? "opacity-75" : "text-ui-fg-muted")}>{hint}</span>}
    </>
  )
  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={clx(className, "text-left")}>
      {body}
    </button>
  )
}

/**
 * Today for the person who makes the custom orders (lib/roles.ts «custom»): start or continue an
 * order, the saves list and the finder, then the custom orders that need moving. Sales and the shop's
 * numbers stay under More.
 */
function CustomToday() {
  const navigate = useNavigate()
  const custom = useQuery({ queryKey: ["hq", "custom"], queryFn: hqApi.customOrders, refetchInterval: 120_000 })
  const picks = usePicks()
  const open = useOpenOrders(t("New order"))
  const today = athensToday()
  const list = custom.data ? custom.data.orders.filter((order) => !order.is_stock) : null
  const active = list?.filter((order) => order.status === "active") ?? null
  const unpaid = active ? active.filter((order) => !depositPaid(order.prepay_status)).length : null
  const toLarry = active ? active.filter((order) => depositPaid(order.prepay_status) && !order.sent_to_larry).length : null
  const toDeliver = list ? list.filter((order) => order.status === "pending").length : null
  const late = active ? active.filter((order) => (daysWithoutParcel(order, today) ?? 0) >= NO_PARCEL_DAYS).length : null
  const saved = picks.data?.picks.length ?? 0
  const value = (count: number | null) => (count == null ? "–" : count)
  const tone = (count: number | null) => (count ? "default" : "muted") as "default" | "muted"

  return (
    <>
      <section className="flex flex-col gap-y-2">
        <SectionLabel>{t("Start")}</SectionLabel>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <Action primary onClick={() => navigate(`/custom/new?w=${newWorkbenchId()}`)} label={t("New custom order")} />
          <Action to="/finder" label={t("Supplier finder")} hint={t("Photo, description or a link")} />
          <Action to="/finder/saved" label={t("Saved")} hint={saved ? t.n(saved, "{n} item", "{n} items") : t("Nothing saved")} />
          <Action to="/payments" label={t("Payment link")} hint={t("Deposit or balance by card")} />
        </div>
      </section>

      {open.length > 0 && (
        <section className="flex flex-col gap-y-2">
          <SectionLabel>{t("Orders you are filling")}</SectionLabel>
          <div className="bg-ui-bg-base shadow-elevation-card-rest divide-ui-border-base flex flex-col divide-y overflow-hidden rounded-lg">
            {open.slice(0, 5).map((order) => (
              <Link key={order.id} to={`/custom/new?w=${encodeURIComponent(order.id)}`} className="hover:bg-ui-bg-base-hover flex items-center gap-x-3 px-4 py-3">
                <span className="min-w-0 flex-1">
                  <span className="txt-compact-small-plus block truncate">{order.label}</span>
                  <span className="txt-compact-xsmall text-ui-fg-muted">{t.n(order.items, "{n} item", "{n} items")}</span>
                </span>
                <span className="txt-compact-small-plus text-ui-fg-interactive shrink-0">{t("Continue")}</span>
                <ChevronRight className="text-ui-fg-muted -ml-2 shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-y-2">
        <SectionLabel>{t("Custom orders to move")}</SectionLabel>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <Stat label={t("Deposit not paid")} value={value(unpaid)} tone={tone(unpaid)} hint={t("Waiting for the customer")} to="/custom?tab=active" />
          <Stat label={t("To send to Larry")} value={value(toLarry)} tone={tone(toLarry)} hint={t("Deposit paid, not ordered yet")} to="/custom?tab=active&f=not_with_larry" />
          <Stat label={t("Custom to deliver")} value={value(toDeliver)} tone={tone(toDeliver)} hint={t("Arrived, not with the customer yet")} to="/custom?tab=pending" />
          <Stat label={t("20+ days, no parcel")} value={value(late)} tone={late ? "default" : "muted"} hint={t("Tell the customer")} to="/custom?tab=active" />
        </div>
      </section>

      <Attention only={CUSTOM_ALERTS} />
    </>
  )
}

/**
 * Today for the delivery person (lib/roles.ts «courier»): the day's route first, what is waiting to
 * be handed over, and his own money (the cash he holds and what the shop owes him, /courier).
 */
function CourierToday() {
  const orders = useQuery({ queryKey: ["hq", "orders"], queryFn: hqApi.orders, refetchInterval: 60_000 })
  const custom = useQuery({ queryKey: ["hq", "custom"], queryFn: hqApi.customOrders, refetchInterval: 120_000 })
  const courier = useQuery({ queryKey: ["hq", "courier"], queryFn: courierApi.page, refetchInterval: 120_000 })
  const toArrange = orders.data?.counts?.to_arrange ?? null
  const customToHand = custom.data
    ? custom.data.orders.filter((order) => order.status === "pending" && !order.is_stock && ["meetup", "home"].includes(deliveryOf(order))).length
    : null
  const open = courier.data?.open
  const value = (count: number | null | undefined) => (count == null ? "–" : count)
  const tone = (count: number | null | undefined) => (count ? "default" : "muted") as "default" | "muted"

  return (
    <>
      <section className="flex flex-col gap-y-2">
        <SectionLabel>{t("Deliveries")}</SectionLabel>
        <Deliveries />
      </section>
      <section className="flex flex-col gap-y-2">
        <SectionLabel>{t("To hand over")}</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          <Stat label={QUEUE_LABEL.to_arrange} value={value(toArrange)} tone={tone(toArrange)} hint={t("Meet-ups and home deliveries")} to="/orders?queue=to_arrange" />
          <Stat label={t("Custom to deliver")} value={value(customToHand)} tone={tone(customToHand)} hint={t("Meet-up or home, arrived")} to="/custom?tab=pending&f=meetup,home" />
        </div>
      </section>
      <section className="flex flex-col gap-y-2">
        <SectionLabel>{t("Your money")}</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          <Stat
            label={t("Cash to hand over")}
            value={open ? money0(open.cash) : "–"}
            tone={open?.cash ? "default" : "muted"}
            hint={open ? t.n(open.meetups + open.homes, "{n} delivery since the last hand-over", "{n} deliveries since the last hand-over") : undefined}
            to="/courier"
          />
          <Stat label={t("Owed to you")} value={courier.data ? money0(courier.data.owed + (open?.pay ?? 0)) : "–"} tone={courier.data?.owed || open?.pay ? "default" : "muted"} hint={t("Your pay, not paid yet")} to="/courier" />
        </div>
      </section>
      <Attention only={COURIER_ALERTS} />
    </>
  )
}
